/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import { FileText, FilePlus, FileX, FileEdit, File, FolderOpen } from 'lucide-react';

export interface DirectoryFileInfo {
  path: string;
  name: string;
  status: 'modified' | 'added' | 'deleted' | 'renamed' | 'untracked' | 'unknown';
  staged: boolean;
}

interface DiffViewerProps {
  diff: string;
  filePath: string;
  fileStatus: 'modified' | 'added' | 'deleted' | 'renamed' | 'untracked' | 'unknown';
  isNewFile?: boolean;
  isDirectory?: boolean;
  directoryFiles?: DirectoryFileInfo[];
  onFileClick?: (filePath: string) => void;
}

interface DiffLine {
  type: 'header' | 'hunk' | 'addition' | 'deletion' | 'context' | 'info';
  content: string;
  oldLineNum?: number;
  newLineNum?: number;
}

/**
 * Parse git diff output into structured lines
 */
function parseDiff(diff: string, isNewFile: boolean): DiffLine[] {
  const lines = diff.split('\n');
  const result: DiffLine[] = [];
  
  // Track line numbers
  let oldLineNum = 0;
  let newLineNum = 0;
  
  for (const line of lines) {
    // New file header (custom format from our server)
    if (line.startsWith('New file:')) {
      result.push({ type: 'header', content: line });
      continue;
    }
    
    // Diff header lines (diff --git, index, ---, +++)
    if (line.startsWith('diff --git') ||
        line.startsWith('index ') ||
        line.startsWith('---') ||
        line.startsWith('+++')) {
      result.push({ type: 'header', content: line });
      continue;
    }
    
    // Hunk header (e.g., @@ -1,5 +1,7 @@)
    if (line.startsWith('@@')) {
      // Parse the hunk header to get line numbers
      const match = line.match(/@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/);
      if (match) {
        oldLineNum = parseInt(match[1], 10);
        newLineNum = parseInt(match[2], 10);
      }
      result.push({ type: 'hunk', content: line });
      continue;
    }
    
    // Binary file notice
    if (line.startsWith('Binary files')) {
      result.push({ type: 'info', content: line });
      continue;
    }
    
    // Addition (starts with +)
    // For new/untracked files displaying raw content, don't parse + as diff syntax
    if (line.startsWith('+') && !isNewFile) {
      result.push({
        type: 'addition',
        content: line.substring(1),
        newLineNum: newLineNum++
      });
      continue;
    }
    
    // Deletion (starts with -)
    // For new/untracked files, lines starting with - are content (e.g., markdown lists), not deletions
    if (line.startsWith('-') && !isNewFile) {
      result.push({
        type: 'deletion',
        content: line.substring(1),
        oldLineNum: oldLineNum++
      });
      continue;
    }
    
    // Context line (starts with space) or plain content for new files
    if (line.startsWith(' ')) {
      result.push({
        type: 'context',
        content: line.substring(1),
        oldLineNum: oldLineNum++,
        newLineNum: newLineNum++
      });
    } else if (isNewFile && line.length > 0) {
      // For new files, all content is additions
      result.push({
        type: 'addition',
        content: line,
        newLineNum: newLineNum++
      });
    } else if (line.trim() === '') {
      // Empty line - could be context or part of new file
      if (isNewFile && result.length > 1) {
        result.push({
          type: 'addition',
          content: '',
          newLineNum: newLineNum++
        });
      } else {
        result.push({
          type: 'context',
          content: '',
          oldLineNum: oldLineNum++,
          newLineNum: newLineNum++
        });
      }
    }
  }
  
  return result;
}

/**
 * Get file icon based on status
 */
function getFileIcon(status: DirectoryFileInfo['status']) {
  switch (status) {
    case 'added':
    case 'untracked':
      return <FilePlus size={16} className="text-green-600" />;
    case 'deleted':
      return <FileX size={16} className="text-red-600" />;
    case 'modified':
      return <FileEdit size={16} className="text-yellow-600" />;
    case 'renamed':
      return <FileText size={16} className="text-blue-600" />;
    default:
      return <File size={16} className="text-gray-500" />;
  }
}

/**
 * Get status badge color
 */
function getStatusBadge(status: DirectoryFileInfo['status']) {
  const colors: Record<string, string> = {
    modified: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    added: 'bg-green-100 text-green-800 border-green-200',
    deleted: 'bg-red-100 text-red-800 border-red-200',
    renamed: 'bg-blue-100 text-blue-800 border-blue-200',
    untracked: 'bg-purple-100 text-purple-800 border-purple-200',
    unknown: 'bg-gray-100 text-gray-600 border-gray-200'
  };
  
  return colors[status] || colors.unknown;
}

/**
 * DirectoryViewer component - displays files in a directory with status colors
 */
function DirectoryViewer({ 
  directoryFiles, 
  directoryPath,
  onFileClick 
}: { 
  directoryFiles: DirectoryFileInfo[];
  directoryPath: string;
  onFileClick?: (filePath: string) => void;
}) {
  if (!directoryFiles || directoryFiles.length === 0) {
    return (
      <div className="directory-viewer-empty p-8 text-center">
        <FolderOpen size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500">No changed files in this directory</p>
      </div>
    );
  }

  // Group files by status
  const addedFiles = directoryFiles.filter(f => f.status === 'added' || f.status === 'untracked');
  const modifiedFiles = directoryFiles.filter(f => f.status === 'modified');
  const deletedFiles = directoryFiles.filter(f => f.status === 'deleted');
  const renamedFiles = directoryFiles.filter(f => f.status === 'renamed');
  const otherFiles = directoryFiles.filter(f => !['added', 'untracked', 'modified', 'deleted', 'renamed'].includes(f.status));

  const renderFileGroup = (title: string, files: DirectoryFileInfo[], colorClass: string) => {
    if (files.length === 0) return null;
    
    return (
      <div className="mb-4">
        <h4 className={`text-sm font-semibold px-3 py-2 ${colorClass} rounded-t-lg border-b`}>
          {title} ({files.length})
        </h4>
        <div className="border border-t-0 rounded-b-lg divide-y divide-gray-100">
          {files.map((file) => (
            <div
              key={file.path}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onFileClick?.(file.path)}
            >
              {getFileIcon(file.status)}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {file.name}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {file.path}
                </div>
              </div>
              <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getStatusBadge(file.status)}`}>
                {file.status.charAt(0).toUpperCase() + file.status.slice(1)}
              </span>
              {file.staged && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                  Staged
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="directory-viewer p-4">
      <div className="mb-4 flex items-center gap-2 text-gray-700">
        <FolderOpen size={20} />
        <span className="font-medium">Directory: {directoryPath}</span>
        <span className="text-sm text-gray-500">({directoryFiles.length} changed files)</span>
      </div>
      
      {renderFileGroup('New Files', addedFiles, 'bg-green-50 text-green-800')}
      {renderFileGroup('Modified Files', modifiedFiles, 'bg-yellow-50 text-yellow-800')}
      {renderFileGroup('Deleted Files', deletedFiles, 'bg-red-50 text-red-800')}
      {renderFileGroup('Renamed Files', renamedFiles, 'bg-blue-50 text-blue-800')}
      {renderFileGroup('Other Changes', otherFiles, 'bg-gray-50 text-gray-700')}
    </div>
  );
}

/**
 * DiffViewer component - renders colored diff output or directory contents
 */
export function DiffViewer({ 
  diff, 
  filePath, 
  fileStatus, 
  isNewFile = false,
  isDirectory = false,
  directoryFiles,
  onFileClick
}: DiffViewerProps) {
  // Handle directory view
  if (isDirectory) {
    return (
      <DirectoryViewer 
        directoryFiles={directoryFiles || []} 
        directoryPath={filePath}
        onFileClick={onFileClick}
      />
    );
  }

  // Handle empty diff
  if (!diff || diff.trim() === '') {
    return (
      <div className="diff-viewer-empty">
        <p className="text-gray-500 text-center py-8">No changes to display</p>
      </div>
    );
  }
  
  // Check if this is a binary file
  const isBinaryFile = diff.includes('Binary files') || diff.includes('Binary file');
  
  if (isBinaryFile) {
    return (
      <div className="diff-viewer-binary">
        <div className="diff-binary-notice">
          Binary file - cannot display diff
        </div>
      </div>
    );
  }
  
  // Detect if this is a new file - show entire content as green
  const isNewFileContent = isNewFile || diff.startsWith('New file:') || fileStatus === 'untracked' || fileStatus === 'added';
  
  if (isNewFileContent) {
    // For new files, show all content as additions (green)
    // Strip "New file:" header if present
    let content = diff;
    if (content.startsWith('New file:')) {
      const firstNewline = content.indexOf('\n');
      content = firstNewline > 0 ? content.substring(firstNewline + 1) : '';
    }
    
    const lines = content.split('\n');
    
    return (
      <div className="diff-viewer">
        <div className="diff-content">
          {lines.map((line, index) => (
            <div key={index} className="diff-line diff-line-addition">
              <span className="diff-line-nums">
                <span className="diff-line-num diff-line-num-old"></span>
                <span className="diff-line-num diff-line-num-new">{index + 1}</span>
              </span>
              <span className="diff-line-prefix">+</span>
              <span className="diff-line-content">
                <code>{line || '\u00A0'}</code>
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // For modified files, parse the diff
  const parsedLines = parseDiff(diff, false);
  
  return (
    <div className="diff-viewer">
      <div className="diff-content">
        {parsedLines.map((line, index) => {
          const lineClass = getLineClass(line.type);
          
          return (
            <div key={index} className={`diff-line ${lineClass}`}>
              {/* Line numbers */}
              <span className="diff-line-nums">
                <span className="diff-line-num diff-line-num-old">
                  {line.oldLineNum ?? ''}
                </span>
                <span className="diff-line-num diff-line-num-new">
                  {line.newLineNum ?? ''}
                </span>
              </span>
              
              {/* Line prefix indicator */}
              <span className="diff-line-prefix">
                {getLinePrefix(line.type)}
              </span>
              
              {/* Line content */}
              <span className="diff-line-content">
                <code>{line.content || '\u00A0'}</code>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getLineClass(type: DiffLine['type']): string {
  switch (type) {
    case 'header':
      return 'diff-line-header';
    case 'hunk':
      return 'diff-line-hunk';
    case 'addition':
      return 'diff-line-addition';
    case 'deletion':
      return 'diff-line-deletion';
    case 'context':
      return 'diff-line-context';
    case 'info':
      return 'diff-line-info';
    default:
      return '';
  }
}

function getLinePrefix(type: DiffLine['type']): string {
  switch (type) {
    case 'addition':
      return '+';
    case 'deletion':
      return '-';
    case 'context':
      return ' ';
    default:
      return '';
  }
}

